import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Spinner, Badge, sessionStatusBadgeMap, observationRatingBadgeMap, claimStatusBadgeMap } from '../../components/UI';
import { KpiCard, SectionIcon, DonutChart, DonutLegend, TrnEmpty as EmptyState } from '../../components/TrainerUI';
import { fmtDate, fmtHours, fmtPct } from '../../utils/format';

function fmtMoney(v) {
  const n = Number(v);
  return isNaN(n) ? '—' : `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function firstName(name = '') { return name.split(' ')[0] || name; }

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getWeekRange(d = new Date()) {
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d); monday.setDate(d.getDate() + diffToMonday);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  return { from: ymd(monday), to: ymd(sunday) };
}

function sessionHours(s) {
  if (!s.start_time || !s.end_time) return 0;
  const [sh, sm] = s.start_time.split(':').map(Number);
  const [eh, em] = s.end_time.split(':').map(Number);
  return Math.max(0, (eh * 60 + em - (sh * 60 + sm)) / 60);
}

function fmtTime(t) {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function sessionLabel(s) {
  return s.module_name ? `${s.module_name} (${s.schedule_name})` : s.schedule_name;
}

// Per-weekday teaching-hours bar chart for the current week — same
// hand-rolled bar pattern as DashboardPage's WeekGlanceChart/EnrollmentYoY
// (grown-in bars via .sms-chart-bar, instant CSS tooltip via .sms-chart-tooltip),
// reused here so the trainer portal isn't just tables and numbers. Today's
// bar is picked out in --sms-stat-blue so a trainer can spot "where am I
// right now" in the week at a glance; other days use --sms-acc.
function WeeklyHoursChart({ schedule }) {
  if (!schedule.length) return <EmptyState icon="bi-bar-chart" message="No sessions scheduled this week" />;
  const { from } = getWeekRange();
  const monday = new Date(`${from}T00:00:00`);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i);
    const dateStr = ymd(d);
    const hours = schedule.filter(s => s.session_date === dateStr).reduce((sum, s) => sum + sessionHours(s), 0);
    return { label: d.toLocaleDateString('en-SG', { weekday: 'short' }), dateStr, hours };
  });
  const max = Math.max(1, ...days.map(d => d.hours));
  const h = 110;
  const todayStr = ymd(new Date());

  return (
    <div className="sms-week-glance">
      <div className="d-flex align-items-end gap-2 sms-week-days" style={{ height: h + 10, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: '0 0 20px 0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
          <div style={{ borderTop: '1px dashed var(--sms-bdr)' }} />
          <div style={{ borderTop: '1px dashed var(--sms-bdr)' }} />
          <div style={{ borderTop: '1px solid var(--sms-bdr)' }} />
        </div>
        {days.map((d, i) => {
          const isToday = d.dateStr === todayStr;
          return (
            <div key={i} className="d-flex flex-column align-items-center" style={{ flex: 1, position: 'relative', minWidth: 0 }}>
              <div className="sms-chart-col" style={{ height: h, width: '100%', background: isToday ? 'color-mix(in srgb, var(--sms-stat-blue) 8%, var(--sms-sur2))' : 'var(--sms-sur2)', borderRadius: 8, padding: '0 clamp(2px, 1.5vw, 6px)' }}>
                <div className="sms-chart-tooltip" style={i >= 4 ? { left: 'auto', right: 'calc(100% + 8px)' } : undefined}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{fmtDate(d.dateStr)}</div>
                  <div>{fmtHours(d.hours)} taught</div>
                </div>
                <div className="d-flex align-items-end justify-content-center h-100">
                  <div className="sms-chart-bar" style={{
                    width: 'clamp(14px, 5vw, 30px)',
                    '--bar-h': `${Math.max((d.hours / max) * (h - 12), d.hours ? 4 : 2)}px`,
                    background: isToday ? 'var(--sms-stat-blue)' : 'var(--sms-acc)',
                    opacity: d.hours ? 1 : .3,
                    animationDelay: `${i * 0.04}s`,
                  }} />
                </div>
              </div>
              <span style={{ fontSize: 12, color: isToday ? 'var(--sms-txt)' : 'var(--sms-txt2)', marginTop: 6, fontWeight: isToday ? 700 : 500 }}>{d.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Same dot tokens the rest of the app already uses for status color (WCAG
// AA-verified against both surface colors, see index.css) — reused here so
// the donut's colors mean the same thing everywhere a trainer sees them.
const CLAIM_STATUS_META = {
  paid:         { label: 'Paid',         color: 'var(--sms-dot-present)' },
  approved:     { label: 'Approved',     color: 'var(--sms-stat-blue)' },
  under_review: { label: 'Pending Verification', color: 'var(--sms-dot-late)' },
  verified:     { label: 'Pending Approval', color: 'var(--sms-stat-purple, var(--sms-dot-late))' },
  rejected:     { label: 'Rejected',     color: 'var(--sms-dot-absent)' },
  cancelled:    { label: 'Cancelled',    color: 'var(--sms-txt3)' },
};

function ClaimsStatusDonut({ claims }) {
  if (!claims.length) return <EmptyState icon="bi-pie-chart" message="No claims submitted yet" />;
  const counts = claims.reduce((acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }), {});
  const segments = Object.entries(CLAIM_STATUS_META).map(([key, meta]) => ({ key, value: counts[key] || 0, ...meta }));
  return (
    <div className="d-flex align-items-center gap-4 flex-wrap">
      <DonutChart segments={segments} centerLabel="Claims" centerValue={claims.length} />
      <DonutLegend segments={segments} />
    </div>
  );
}

function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

// Left-border color per session status — same left-accent language as the
// rest of the dashboard, so a row's state reads before its text does.
const SESSION_ACCENT = {
  ongoing:   'var(--sms-stat-blue)',
  scheduled: 'var(--sms-acc)',
  ended:     'var(--sms-txt3)',
  cancelled: 'var(--sms-stat-red)',
};

function SessionTimeline({ sessions, showDate = false }) {
  return (
    <div>
      {sessions.map(s => (
        <div key={s.id} className={`trn-timeline-row ${s.status === 'ended' ? 'is-ended' : ''}`}
          style={{ '--accent': SESSION_ACCENT[s.status] || 'var(--sms-acc)' }}>
          <div className="trn-timeline-time">
            {showDate ? fmtDate(s.session_date) : fmtTime(s.start_time)}
            <small>{showDate ? `${fmtTime(s.start_time)} – ${fmtTime(s.end_time)}` : `to ${fmtTime(s.end_time)}`}</small>
          </div>
          <div className="trn-timeline-main">
            {sessionLabel(s)}
            <small><i className="bi bi-geo-alt" /> {s.venue_name || '—'}</small>
          </div>
          {!showDate && <Badge value={s.status} map={sessionStatusBadgeMap} />}
        </div>
      ))}
    </div>
  );
}

function AttentionRow({ to, state, icon, color, text, sub }) {
  return (
    <Link className="trn-attn-row" to={to} state={state} style={{ '--accent': color }}>
      <span className="trn-attn-icon"><i className={`bi ${icon}`} /></span>
      <span className="trn-attn-text">{text}{sub && <small>{sub}</small>}</span>
      <i className="bi bi-chevron-right trn-attn-go" />
    </Link>
  );
}

const EMPTY_RES = { data: { data: [] } };

export default function TrainerDashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [observations, setObservations] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [claims, setClaims] = useState([]);
  const [pendingClaims, setPendingClaims] = useState([]);
  const [markingSummary, setMarkingSummary] = useState([]);
  const [scheduleTab, setScheduleTab] = useState('today');

  useEffect(() => {
    let cancelled = false;
    const { from, to } = getWeekRange();
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    (async () => {
      try {
        // Assessments/Claims are admin-gated per trainer — skip the request
        // entirely (and degrade to an empty list) when not granted, rather
        // than letting a 403 there take down the rest of the dashboard.
        // Upcoming is capped server-side to 5 rows (limit=5) so the "Upcoming"
        // tab stays a quick lookup instead of pulling the trainer's whole future.
        // Component-marks summary has no role gate (same as the page itself) —
        // access is entirely governed by trainer_marking_permissions grants,
        // so a trainer with none just gets an empty list back.
        const [sch, upc, obs, asm, clm, pclm, mks] = await Promise.all([
          api.get(`/trainer/schedule?from_date=${from}&to_date=${to}`).catch(() => EMPTY_RES),
          api.get(`/trainer/schedule?from_date=${ymd(tomorrow)}&limit=5`).catch(() => EMPTY_RES),
          api.get('/trainer/observations').catch(() => EMPTY_RES),
          api.get('/trainer/assessments').catch(() => EMPTY_RES),
          api.get('/trainer/claims').catch(() => EMPTY_RES),
          api.get('/trainer/claims/pending').catch(() => EMPTY_RES),
          api.get('/trainer/component-marks/summary').catch(() => EMPTY_RES),
        ]);
        if (cancelled) return;
        setSchedule(sch.data.data || []);
        setUpcoming(upc.data.data || []);
        setObservations(obs.data.data || []);
        setAssessments(asm.data.data || []);
        setClaims(clm.data.data || []);
        setPendingClaims(pclm.data.data || []);
        setMarkingSummary(mks.data.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="trn-page"><Spinner /></div>;

  const today = ymd(new Date());
  const todaySessions = schedule.filter(s => s.session_date === today);
  const weeklyHours = schedule.reduce((sum, s) => sum + sessionHours(s), 0);
  const pendingAssessments = assessments.filter(a => a.status !== 'graded').length;
  const activeClaim = claims.find(c => ['under_review', 'verified', 'approved'].includes(c.status));
  const recentObservations = observations.slice(0, 3);
  const claimablePending = pendingClaims.filter(p => p.claimable);
  const claimablePendingAmount = claimablePending.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const topPendingClaims = pendingClaims.slice(0, 4);
  const ongoingNow = todaySessions.find(s => s.status === 'ongoing');
  const pendingMarkingTotal = markingSummary.reduce((sum, m) => sum + m.pending_students, 0);
  const modulesNeedingMarking = markingSummary.filter(m => m.pending_students > 0);

  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nextToday = todaySessions.find(s => s.status === 'scheduled' && toMinutes(s.start_time) > nowMin);
  const heroSession = ongoingNow || nextToday || upcoming[0] || null;
  const heroIsLive = !!ongoingNow && heroSession === ongoingNow;
  const heroIsToday = heroSession && heroSession.session_date === today;
  const heroStart = heroSession ? toMinutes(heroSession.start_time) : 0;
  const heroEnd = heroSession ? toMinutes(heroSession.end_time) : 0;
  const heroPct = heroIsLive && heroEnd > heroStart ? Math.min(100, Math.max(0, Math.round((nowMin - heroStart) / (heroEnd - heroStart) * 100))) : 0;
  const heroNext = heroIsLive ? todaySessions.find(s => s.status === 'scheduled' && toMinutes(s.start_time) > nowMin) : null;

  const attentionRows = [];
  if (claimablePending.length > 0) {
    attentionRows.push({ key: 'claim', to: '/trainer/claim', state: { autoSelectAll: true }, icon: 'bi-receipt', color: 'var(--sms-stat-red)',
      text: `${claimablePending.length} session${claimablePending.length === 1 ? '' : 's'} ready to claim`, sub: `Worth ${fmtMoney(claimablePendingAmount)} — submit to get paid` });
  }
  if (pendingMarkingTotal > 0) {
    attentionRows.push({ key: 'mark', to: '/trainer/component-marks', icon: 'bi-diagram-3', color: 'var(--sms-stat-amber)',
      text: `${pendingMarkingTotal} student${pendingMarkingTotal === 1 ? '' : 's'} awaiting marks`, sub: `${modulesNeedingMarking.length} module(s)` });
  }
  if (pendingAssessments > 0) {
    attentionRows.push({ key: 'asm', to: '/trainer/assessment', icon: 'bi-journal-check', color: 'var(--sms-stat-violet)',
      text: `${pendingAssessments} assessment${pendingAssessments === 1 ? '' : 's'} pending`, sub: `of ${assessments.length} total` });
  }

  return (
    <>
      <div className="trn-topbar trn-topbar-glow">
        <div className="d-flex align-items-center gap-3">
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: 'color-mix(in srgb, var(--sms-acc) 16%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="bi bi-mortarboard-fill" style={{ color: 'var(--sms-acc)', fontSize: 20 }} />
          </div>
          <div>
            <div className="trn-topbar-title d-flex align-items-center gap-2">
              Welcome back, {firstName(user?.full_name)}
              {ongoingNow && (
                <span className="badge rounded-pill badge-late d-inline-flex align-items-center gap-1" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  <span className="sms-live-pulse" />In class now
                </span>
              )}
            </div>
            <div className="trn-topbar-sub">Overview of today's teaching load and pending items</div>
          </div>
        </div>
      </div>
      <div className="trn-page">
        <div className="trn-dash-grid">
          <div className="trn-hero" style={{ '--accent': heroIsLive ? 'var(--sms-stat-blue)' : 'var(--sms-acc)' }}>
            {heroSession ? (
              <>
                <div className="trn-hero-eyebrow">
                  {heroIsLive ? <><span className="sms-live-pulse" /> Happening now</> : <><i className="bi bi-arrow-right-circle" /> {heroIsToday ? 'Up next today' : 'Next class'}</>}
                </div>
                <div className="trn-hero-title">{sessionLabel(heroSession)}</div>
                <div className="trn-hero-meta">
                  <span><i className="bi bi-clock" />{!heroIsToday && `${fmtDate(heroSession.session_date)} · `}{fmtTime(heroSession.start_time)} – {fmtTime(heroSession.end_time)}</span>
                  <span><i className="bi bi-geo-alt" />{heroSession.venue_name || '—'}</span>
                </div>
                {heroIsLive && (
                  <>
                    <div className="trn-progress"><span style={{ width: `${heroPct}%` }} /></div>
                    <div className="trn-progress-cap">
                      <span>{fmtPct(heroPct)} complete</span>
                      <span>{heroNext ? `Next: ${fmtTime(heroNext.start_time)}` : 'Last class today'}</span>
                    </div>
                  </>
                )}
              </>
            ) : (
              <EmptyState icon="bi-cup-hot" color="var(--sms-acc)" message="No upcoming classes — enjoy the break" />
            )}
          </div>

          <div className="trn-card trn-attn">
            <div className="trn-card-head">
              <h3 className="d-flex align-items-center"><SectionIcon icon="bi-bell" color="var(--sms-stat-red)" />Needs your attention</h3>
              {attentionRows.length > 0 && <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{attentionRows.length} item{attentionRows.length === 1 ? '' : 's'}</span>}
            </div>
            <div className="trn-card-body">
              {attentionRows.length === 0
                ? <EmptyState icon="bi-check2-circle" color="var(--sms-dot-present)" message="You're all caught up" />
                : attentionRows.map(({ key, ...r }) => <AttentionRow key={key} {...r} />)}
            </div>
          </div>
        </div>

        <div className="trn-stat-row">
          <KpiCard label="Today's Classes" value={todaySessions.length} icon="bi-calendar3" color="var(--sms-stat-blue)" live={!!ongoingNow}
            caption={todaySessions[0] ? `Next: ${fmtTime(todaySessions[0].start_time)} · ${todaySessions[0].venue_name || '—'}` : 'No classes today'} />
          <KpiCard label="Teaching Hours (Week)" value={weeklyHours} decimals={1} icon="bi-clock-history" color="var(--sms-stat-cyan)"
            caption={`${schedule.length} session(s) this week`} />
          <KpiCard label="Pending Assessments" value={pendingAssessments} icon="bi-journal-check" color="var(--sms-stat-violet)"
            caption={assessments.length ? `of ${assessments.length} total` : 'None yet'} />
          <KpiCard label="Active Claim" icon="bi-receipt" color="var(--sms-stat-amber)"
            value={activeClaim ? <Badge value={activeClaim.status} map={claimStatusBadgeMap} /> : '—'}
            caption={claims.length ? `${claims.length} total submitted` : 'No claims yet'} />
        </div>

        <div className="d-flex flex-wrap gap-3" style={{ marginBottom: 18 }}>
          <div className="trn-card" style={{ flex: '2 1 380px', margin: 0 }}>
            <div className="trn-card-head">
              <h3 className="d-flex align-items-center"><SectionIcon icon="bi-bar-chart-line" color="var(--sms-stat-cyan)" />This Week's Teaching Hours</h3>
              <span>{schedule.length} session(s)</span>
            </div>
            <div className="trn-card-body">
              <WeeklyHoursChart schedule={schedule} />
            </div>
          </div>
          {(
            <div className="trn-card" style={{ flex: '1 1 280px', margin: 0 }}>
              <div className="trn-card-head">
                <h3 className="d-flex align-items-center"><SectionIcon icon="bi-pie-chart-fill" color="var(--sms-stat-amber)" />Claims by Status</h3>
              </div>
              <div className="trn-card-body">
                <ClaimsStatusDonut claims={claims} />
              </div>
            </div>
          )}
        </div>

        <div className="trn-card">
          <div className="trn-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-calendar3" color="var(--sms-stat-blue)" />Schedule</h3>
            <span>{scheduleTab === 'today' ? fmtDate(today) : 'Next 5 sessions'}</span>
          </div>
          <div className="trn-card-body">
            <div className="sms-tabs" style={{ marginBottom: 12 }}>
              <button type="button" className={`sms-tab ${scheduleTab === 'today' ? 'active' : ''}`} onClick={() => setScheduleTab('today')}>
                Today {todaySessions.length > 0 && `(${todaySessions.length})`}
              </button>
              <button type="button" className={`sms-tab ${scheduleTab === 'upcoming' ? 'active' : ''}`} onClick={() => setScheduleTab('upcoming')}>
                Upcoming {upcoming.length > 0 && `(${upcoming.length})`}
              </button>
            </div>
            {scheduleTab === 'today' ? (
              todaySessions.length === 0 ? (
                <EmptyState icon="bi-calendar-x" message="No classes scheduled today" />
              ) : (
                <SessionTimeline sessions={todaySessions} />
              )
            ) : (
              upcoming.length === 0 ? (
                <EmptyState icon="bi-calendar-week" message="No upcoming classes scheduled" />
              ) : (
                <SessionTimeline sessions={upcoming} showDate />
              )
            )}
          </div>
        </div>

        {(
          <div className="trn-card">
            <div className="trn-card-head">
              <h3 className="d-flex align-items-center"><SectionIcon icon="bi-hourglass-split" color="var(--sms-stat-amber)" />Pending Claims</h3>
              {pendingClaims.length > 0 && (
                <Link className="trn-link-pill" to="/trainer/claim">
                  View all
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              )}
            </div>
            <div className="trn-card-body">
              {pendingClaims.length === 0 ? (
                <EmptyState icon="bi-check2-circle" message="No ended sessions waiting on a claim" />
              ) : (
                <table className="trn-table">
                  <thead><tr><th>Date</th><th>Module</th><th>Intake</th><th className="num">Hours</th><th className="num">Amount</th><th></th></tr></thead>
                  <tbody>
                    {topPendingClaims.map(p => (
                      <tr key={p.session_id}>
                        <td>{fmtDate(p.session_date)}</td>
                        <td>{p.module_name}</td>
                        <td>{p.intake_code || '—'}</td>
                        <td className="num">{fmtHours(p.hours)}</td>
                        <td className="num">{p.amount !== null ? fmtMoney(p.amount) : '—'}</td>
                        <td>
                          {p.claimable ? (
                            <Link className="trn-link-pill" to="/trainer/claim">Submit
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                            </Link>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }} title="No fee rate has been set up for this session yet">No rate set</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {markingSummary.length > 0 && (
          <div className="trn-card">
            <div className="trn-card-head">
              <h3 className="d-flex align-items-center"><SectionIcon icon="bi-diagram-3" color="var(--sms-stat-red)" />Pending Marking</h3>
              <Link className="trn-link-pill" to="/trainer/component-marks">
                Mark now
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
              </Link>
            </div>
            <div className="trn-card-body">
              {modulesNeedingMarking.length === 0 ? (
                <EmptyState icon="bi-check2-circle" message="All students marked across your assigned modules" />
              ) : (
                <table className="trn-table">
                  <thead><tr><th>Module</th><th>Intake</th><th>Exam</th><th className="num">Pending</th><th></th></tr></thead>
                  <tbody>
                    {modulesNeedingMarking.map(m => (
                      <tr key={`${m.intake_code}-${m.module_code}-${m.exam_type}`}>
                        <td>{m.module_name || m.module_code}</td>
                        <td>{m.intake_code}</td>
                        <td style={{ textTransform: 'capitalize' }}>{m.exam_type}</td>
                        <td className="num" style={{ fontWeight: 700, color: 'var(--sms-stat-red)' }}>{m.pending_students} / {m.total_students}</td>
                        <td>
                          <Link className="trn-link-pill" to="/trainer/component-marks">Mark
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        <div className="trn-card">
          <div className="trn-card-head"><h3 className="d-flex align-items-center"><SectionIcon icon="bi-eye" color="var(--sms-stat-violet)" />Recent Observations</h3></div>
          <div className="trn-card-body">
            {recentObservations.length === 0 ? (
              <EmptyState icon="bi-eye" message="No observations recorded yet" />
            ) : (
              <table className="trn-table">
                <thead><tr><th>Date</th><th>Module</th><th>Observed By</th><th>Rating</th></tr></thead>
                <tbody>
                  {recentObservations.map(o => (
                    <tr key={o.id}>
                      <td>{fmtDate(o.observation_date)}</td>
                      <td>{o.module_name || o.module_code || '—'}</td>
                      <td>{o.observed_by_name}</td>
                      <td><Badge value={o.rating} map={observationRatingBadgeMap} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
